<template>
  <LayoutCard rounded>
    <div class="d-flex align-center justify-space-between mb-4">
      <div class="tp-text-subtitle">
        {{ $t('components.requestInvoice.scanReceipt.title') }}
      </div>
      <button
        type="button"
        class="scan-cta-button tp-text-body"
        @click.stop.prevent="openQrScanner"
      >
        <span>{{
          $t('components.requestInvoice.scanReceipt.scanButton')
        }}</span>
      </button>
    </div>

    <div class="tp-text-body mb-3">
      {{ $t('components.requestInvoice.scanReceipt.instruction') }}
    </div>

    <v-form ref="form">
      <v-text-field
        v-model="formData.SM"
        color="black"
        :label="$t('components.requestInvoice.receiptScan.securityModuleLabel')"
        :placeholder="
          $t('components.requestInvoice.receiptScan.securityModulePlaceholder')
        "
        type="text"
        maxlength="100"
        autocomplete="off"
      ></v-text-field>

      <v-text-field
        v-model="formData.RC"
        color="black"
        :label="$t('components.requestInvoice.receiptScan.receiptCodeLabel')"
        :placeholder="
          $t('components.requestInvoice.receiptScan.receiptCodePlaceholder')
        "
        type="text"
        maxlength="100"
        autocomplete="off"
      ></v-text-field>
    </v-form>

    <Modal
      :is-open="showQrScanner"
      :persistent="true"
      :show-close-button="true"
      :close-label="$t('generic.cancel')"
      :title="$t('components.requestInvoice.scanReceipt.modalTitle')"
      @close-click="closeQrScanner"
    >
      <template slot="content">
        <div class="qr-scanner-modal-content">
          <div class="qr-reader-wrapper">
            <qrcode-stream
              v-if="showQrScanner"
              class="qr-reader"
              :camera="cameraState"
              :paused="scanCompleted"
              :video-constraints="cameraConstraints"
              @decode="onQrCodeDecoded"
              @init="onCameraInit"
              @error="onCameraError"
            ></qrcode-stream>
            <div
              v-if="showQrScanner && !cameraReady"
              class="qr-camera-loader-overlay"
            >
              <v-progress-circular
                indeterminate
                color="black"
                size="48"
              ></v-progress-circular>
            </div>
            <div v-if="cameraReady" class="qr-overlay">
              <div class="qr-scan-square"></div>
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </LayoutCard>
</template>

<script>
import Modal from '@/components/common/Modal.vue';
import { QrcodeStream } from 'vue-qrcode-reader';

export default {
  name: 'ScanReceiptFormV2',
  components: {
    Modal,
    QrcodeStream,
  },
  props: {
    initialReceiptData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        SM: this.initialReceiptData?.SM ?? null,
        RC: this.initialReceiptData?.RC ?? null,
      },
      showQrScanner: false,
      cameraState: 'off',
      cameraReady: false,
      scanCompleted: false,
      cameraInitError: null,
      cameraConstraints: {
        facingMode: 'environment',
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        aspectRatio: { ideal: 1.0 },
        frameRate: { ideal: 15, max: 30 },
        resizeMode: 'crop-and-scale',
        advanced: [
          {
            focusMode: 'continuous',
          },
        ],
      },
    };
  },
  watch: {
    showQrScanner(newVal) {
      if (newVal) {
        this.cameraState = 'auto';
      } else {
        this.cameraState = 'off';
      }
    },
  },
  beforeDestroy() {
    this.closeQrScanner();
  },
  methods: {
    openQrScanner() {
      this.showQrScanner = true;
      this.cameraReady = false;
      this.scanCompleted = false;
      this.cameraInitError = null;
      this.cameraState = 'auto';
    },
    closeQrScanner() {
      this.cameraState = 'off';
      this.showQrScanner = false;
      this.cameraReady = false;
      this.scanCompleted = false;
    },
    onCameraInit(promise) {
      promise
        .then(() => {
          this.cameraInitError = null;
          this.cameraReady = true;
        })
        .catch((error) => {
          console.error('Error initializing camera:', error);
          this.cameraInitError = error;
          this.showQrScanner = false;
          this.$store.dispatch('showToastInstantly', {
            title: this.$i18n.translate('generic.error'),
            description: this.$i18n.translate(
              'components.requestInvoice.receiptScan.cameraErrorDescription'
            ),
            isError: true,
          });
        });
    },
    onCameraError(error) {
      console.error('Camera error:', error);
      this.cameraInitError = error;
    },
    onQrCodeDecoded(decodedString) {
      if (this.scanCompleted) {
        return;
      }
      this.onQrCodeScanned(decodedString);
    },
    onQrCodeScanned(decodedText) {
      try {
        if (decodedText.includes('kvitas.vmi.lt')) {
          const parsed = this.parseVmiReceiptUrl(decodedText);
          if (parsed) {
            this.closeQrScanner();
            this.emitReceiptScanned();
            return;
          }
        } else {
          this.showInvalidQrCodeToast();
        }
      } catch (error) {
        console.error('Error parsing QR code:', error);
        this.showInvalidQrCodeToast();
      }
    },
    parseVmiReceiptUrl(url) {
      try {
        const urlObj = new URL(url);
        const params = new URLSearchParams(urlObj.search);

        const nr = params.get('NR');
        if (!nr) {
          return false;
        }

        const sm = params.get('SM');
        const rc = params.get('RC');

        this.formData.SM = sm || null;
        this.formData.RC = rc || null;

        return true;
      } catch (error) {
        console.error('Error parsing VMI receipt URL:', error);
        const smMatch = url.match(/[?&]SM=([^&]+)/);
        const rcMatch = url.match(/[?&]RC=([^&]+)/);
        if (smMatch && smMatch[1]) {
          this.formData.SM = smMatch[1];
        }
        if (rcMatch && rcMatch[1]) {
          this.formData.RC = rcMatch[1];
        }
        return smMatch || rcMatch;
      }
    },
    showInvalidQrCodeToast() {
      this.$store.dispatch('showToastInstantly', {
        title: this.$i18n.translate(
          'components.requestInvoice.receiptScan.invalidQrCodeTitle'
        ),
        description: this.$i18n.translate(
          'components.requestInvoice.receiptScan.invalidQrCodeDescription'
        ),
        isError: true,
      });
    },
    emitReceiptScanned() {
      this.$emit('receipt-scanned', {
        SM: this.formData.SM,
        RC: this.formData.RC,
      });
    },
    validate() {
      return this.$refs.form?.validate() || false;
    },
    getReceiptData() {
      return {
        SM: this.formData.SM,
        RC: this.formData.RC,
      };
    },
  },
};
</script>

<style scoped lang="scss">
.scan-cta-button {
  background: none;
  border: none;
  color: $color-primary;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
}

.qr-scanner-modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0;
}

.qr-reader-wrapper {
  position: relative;
  width: 100%;
  max-width: 400px;
  max-height: 600px;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 16px;
}

.qr-reader {
  width: 100%;
  border-radius: 8px !important;
  overflow: hidden;

  ::v-deep video {
    border-radius: 8px;
    width: 100%;
    height: auto;
  }

  ::v-deep canvas {
    border-radius: 8px;
  }
}

.qr-camera-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  z-index: 1;
}

.qr-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;
}

.qr-scan-square {
  width: 100px;
  height: 100px;
  border: 3px solid #ffffff;
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
}
</style>
